import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
} from '@mui/material';
import Search from '@mui/icons-material/Search';
import SupervisorAccount from '@mui/icons-material/SupervisorAccount';
import { useTranslation } from 'react-i18next';
import { ViewToolbar } from '@/components/ViewToolbar';
import { ValidationErrorBanner } from '@/components/forms/ValidationErrorBanner';
import { ValidationError } from '@/lib/validationErrors';
import { getErrorMessage } from '@/lib/apiClient';
import { identityUserService } from '@/services/identity/identityService';
import { authService } from '@/services/auth/authService';
import { useImpersonation } from '@/core/hooks/useImpersonation';
import { UserLookup } from '@/types/identity/Identity';
import { UserAccountTypeChip } from '@/core/UserAccountTypeChip';
import { parseUserAccountType } from '@/core/userAccountType';

/**
 * "Run as user" — choosing whom to act as, reachable with the impersonation
 * permission alone.
 *
 * This exists because the action was unreachable for the one role that is
 * supposed to live in it: a support session holds Nexo.Users.Impersonate and
 * nothing else (D14), while the Users page is gated on AbpIdentity.Users. The
 * fix is deliberately NOT to widen that gate — reading the customer's whole
 * directory outside an audited impersonation session is what D14 exists to
 * prevent — but to give the action its own narrow surface. Administrators reach
 * the same page; they simply also have the fuller one.
 *
 * The server re-checks every impersonation rule at token issuance. The filtering
 * here (no self, no inactive) only avoids offering a click that can only fail.
 */
export function RunAsUserPage() {
  const navigate = useNavigate();
  const impersonate = useImpersonation();
  const { t } = useTranslation();

  const [filter, setFilter] = useState('');
  const [results, setResults] = useState<UserLookup[] | null>(null);
  // Starts true: the page loads its first page immediately, so the very first
  // paint is the spinner and never an empty "no matches".
  const [isSearching, setIsSearching] = useState(true);
  const [startingFor, setStartingFor] = useState<string | null>(null);
  const [errors, setErrors] = useState<ValidationError[]>([]);

  // `sub`, not `id` — the identity comes from the decoded access token.
  const currentUserId = authService.getCurrentUser()?.sub;

  // Which search is the current one. The unfiltered load fired on mount can
  // land after a search the operator typed straight away; without this it would
  // overwrite their results with the full list.
  const latestSearch = useRef(0);

  const runSearch = useCallback(
    async (term: string) => {
      const seq = ++latestSearch.current;
      setIsSearching(true);
      setErrors([]);
      try {
        const found = await identityUserService.searchRunAsCandidates(term);
        if (seq !== latestSearch.current) return;
        setResults(found.filter((u) => u.id !== currentUserId));
      } catch (error: unknown) {
        if (seq !== latestSearch.current) return;
        setResults(null);
        setErrors([{ field: 'form', message: getErrorMessage(error), instancePath: '#', members: [] }]);
      } finally {
        if (seq === latestSearch.current) setIsSearching(false);
      }
    },
    [currentUserId],
  );

  // Whom to run as is a short list in practice, so show it rather than making
  // the operator search for a name they may not know yet. The server returns
  // the first page (ordered by user name) for an empty filter.
  useEffect(() => {
    void runSearch('');
  }, [runSearch]);

  const runAs = useCallback(
    async (userId: string) => {
      setStartingFor(userId);
      setErrors([]);
      try {
        await impersonate(userId);
      } catch (error: unknown) {
        setErrors([{ field: 'form', message: getErrorMessage(error), instancePath: '#', members: [] }]);
      } finally {
        setStartingFor(null);
      }
    },
    [impersonate],
  );

  const displayName = (u: UserLookup) =>
    [u.name, u.surname].filter(Boolean).join(' ') || u.userName;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ValidationErrorBanner
        errors={errors}
        onExpand={() => { /* single-message banner — nothing to expand into */ }}
        onClear={() => setErrors([])}
      />

      <ViewToolbar showBackButton onBack={() => navigate('/')} />

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Typography variant="h5" gutterBottom>
          {t('Menu:Core.RunAsUser')}
        </Typography>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              void runSearch(filter);
            }}
            sx={{ display: 'flex', gap: 1 }}
          >
            <TextField
              label={t('RunAsUser:SearchLabel')}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              size="small"
              fullWidth
              autoComplete="off"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            {/*
              Not disabled while a search runs: the first one starts on mount,
              and locking the operator out of the box until it lands is the
              opposite of the point. Overlapping searches are safe — the later
              one supersedes the earlier by sequence number.
            */}
            <Button type="submit" variant="contained">
              {t('RunAsUser:Search')}
            </Button>
          </Box>
        </Paper>

        {isSearching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {!isSearching && results !== null && results.length === 0 && (
          <Alert severity="info">{t('RunAsUser:NoMatches')}</Alert>
        )}

        {!isSearching && results !== null && results.length > 0 && (
          <Paper>
            <List dense>
              {results.map((u) => {
                const inactive = u.isActive === false;
                const accountType = parseUserAccountType(u.accountType);
                return (
                  <ListItem
                    key={u.id}
                    // The rows are as wide as the page, so the name and the
                    // button that belongs to it sit far apart. Tinting the row
                    // under the cursor is what ties them together.
                    sx={
                      inactive
                        ? undefined
                        : { '&:hover': { backgroundColor: 'action.hover' } }
                    }
                    secondaryAction={
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<SupervisorAccount />}
                        disabled={inactive || startingFor !== null}
                        onClick={() => void runAs(u.id)}
                      >
                        {startingFor === u.id ? t('RunAsUser:Starting') : t('RunAsUser:RunAs')}
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {displayName(u)}
                          {/*
                            Whom you are about to become matters here more than
                            anywhere else in the app: a support identity or a
                            break-glass account is not an ordinary employee to
                            step into unremarked.
                          */}
                          {accountType && <UserAccountTypeChip accountType={accountType} />}
                          {inactive && (
                            <Chip size="small" label={t('RunAsUser:Inactive')} color="default" />
                          )}
                        </Box>
                      }
                      secondary={u.userName}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        )}
      </Box>
    </Box>
  );
}
